// Resync on Claims → Runs: compare a run with what AxisCare says now, review
// what is new or changed, and apply the chosen changes.
//
// Moved out of pages/finance/BillingRun.js on 2026-10-20. Nothing is written
// until Apply; the classification is lib/billing/resync.js and the writes are
// lib/billing/runs.js applyResync().

import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { loadBillingCatalog } from '../../../lib/billingCodes';
import { EXPECTED_CHARGE_MULTIPLIER } from '../../../lib/edi/preflight';
import { clientOf, lineHours } from '../../../lib/billing/claimLines';
import { classifyResync } from '../../../lib/billing/resync';
import { applyResync as applyResyncToRun, runsNewlyOverlapped } from '../../../lib/billing/runs';
import { destinationForPayer } from '../../../lib/serviceModel';
import { fetchBillingVisits, billableVisits, buildClientMap, loadAuthHistory } from './visitData';
import { fmt$2, fmtDate } from './format';

// Hours and dollars are compared to the cent: a difference below that is
// rounding, not a change, and used to show as "+$0.00".
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Hours on a basis — lib/billing/claimLines.js, the rule the Monday run uses too.
const calcHours = lineHours;

/**
 * @param {object}   p.run        the run being resynced
 * @param {Function} p.onClose
 * @param {Function} p.onApplied  (runId) after changes are written
 */
export default function ResyncModal({ run: resyncRun, onClose, onApplied }) {
  const [resyncFrom,     setResyncFrom]     = useState(resyncRun.date_from);
  const [resyncTo,       setResyncTo]       = useState(resyncRun.date_to);
  const [resyncFetching, setResyncFetching] = useState(false);
  const [resyncVisits,   setResyncVisits]   = useState([]); // { visit, status: 'new'|'updated'|'existing' }
  const [resyncClientMap,setResyncClientMap]= useState({});
  const [resyncAuthHist, setResyncAuthHist] = useState({});
  const [resyncSelected, setResyncSelected] = useState({});
  const [resyncStep,     setResyncStep]     = useState('date'); // date | review | done
  const [resyncing,      setResyncing]      = useState(false);
  const [resyncSummary,  setResyncSummary]  = useState(null);
  // Lines added by a resync bill on the basis the run was created with, so a
  // run stays internally consistent no matter when a line joined it.
  const resyncBasis = resyncRun?.hours_basis || 'scheduled';
  const closeResync = onClose;

  const fetchResyncVisits = async () => {
    if (!resyncFrom || !resyncTo) return;
    setResyncFetching(true);
    try {
      // Widening a run must not take in days another run already billed.
      const clash = await runsNewlyOverlapped(supabase, { run: resyncRun, dateFrom: resyncFrom, dateTo: resyncTo });
      if (clash.length) {
        alert(`These dates overlap the run for ${clash.map(r => `${fmtDate(r.date_from)} – ${fmtDate(r.date_to)}`).join(', ')}. `
          + 'Resync can only take in days no other run has billed. Narrow the dates, or resync that run instead.');
        return;
      }
      // Get existing lines for this run
      const { data: existingLines } = await supabase
        .from('claim_lines').select('*').eq('billing_run_id', resyncRun.id);

      const arr = await fetchBillingVisits(resyncFrom, resyncTo);
      // Resync bills on the basis the run was created with, never the wizard's
      // current selection — a run that went out on scheduled hours must stay
      // internally consistent when lines are added to it later.
      const runBasis = resyncRun?.hours_basis || 'scheduled';
      const cMap = await buildClientMap(billableVisits(arr, runBasis));
      // A run holds one destination. A CareSource visit does not belong in a
      // GAMMIS run however the week is resynced, and vice versa.
      const runDest = resyncRun?.payer || 'gammis';
      const completed = billableVisits(arr, runBasis)
        .filter(v => destinationForPayer(clientOf(v, cMap).payer) === runDest);

      const hist = await loadAuthHistory(cMap);
      setResyncClientMap(cMap); setResyncAuthHist(hist);

      // Match each visit to its line, by visit id then name and date — the
      // rule is lib/billing/resync.js.
      const classified = classifyResync({ visits: completed, existingLines: existingLines || [], basis: runBasis });

      setResyncVisits(classified);

      // Pre-select new + updated
      const sel = {};
      classified.forEach((c, i) => { sel[i] = c.status !== 'existing'; });
      setResyncSelected(sel);
      setResyncStep('review');
    } catch (err) { alert('Failed to fetch AxisCare data: ' + err.message); }
    finally { setResyncFetching(false); }
  };

  const applyResync = async () => {
    setResyncing(true);
    try {
      const catalog = await loadBillingCatalog();
      const { added, updated } = await applyResyncToRun(supabase, {
        run: resyncRun,
        items: resyncVisits.filter((_, i) => resyncSelected[i]),
        clientMap: resyncClientMap, authHist: resyncAuthHist, catalog,
        dateFrom: resyncFrom, dateTo: resyncTo,
        defaultMultiplier: EXPECTED_CHARGE_MULTIPLIER, now: new Date().toISOString(),
      });

      setResyncSummary({ added, updated });
      setResyncStep('done');
      onApplied(resyncRun.id);
    } catch (err) { alert('Resync failed: ' + err.message); }
    finally { setResyncing(false); }
  };

  const resyncNew      = resyncVisits.filter(c => c.status === 'new');
  const resyncUpdated  = resyncVisits.filter(c => c.status === 'updated');
  const resyncExisting = resyncVisits.filter(c => c.status === 'existing');

  return (
      <div className="modal-overlay drawer" onMouseDown={e => e.target === e.currentTarget && closeResync()}>
        <div className="modal" style={{ maxWidth: resyncStep === 'review' ? 800 : 480 }} onMouseDown={e => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <div className="modal-title">Resync with AxisCare</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                Run: {fmtDate(resyncRun.date_from)} – {fmtDate(resyncRun.date_to)} · {resyncRun.total_lines} lines
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={closeResync}>✕</button>
          </div>

          <div className="modal-body">
            {resyncStep === 'date' && (
              <>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
                  Resync pulls fresh visit data from AxisCare. You can also adjust the date range to capture additional days.
                  New and changed visits will be highlighted for your review before any changes are applied.
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">From date</label>
                    <input type="date" className="form-input" value={resyncFrom} onChange={e => setResyncFrom(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">To date</label>
                    <input type="date" className="form-input" value={resyncTo} onChange={e => setResyncTo(e.target.value)} />
                  </div>
                </div>
                <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--info-bg)', borderRadius: 8, fontSize: 12, color: 'var(--info)' }}>
                  <strong>What resync does:</strong> Adds new completed visits not yet in this run · Updates hours/rates for existing visits that changed · Existing lines with no changes are left as-is.
                </div>
              </>
            )}

            {resyncStep === 'review' && (
              <>
                {/* Summary chips */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[
                    { label: `${resyncNew.length} New visits`, color: 'var(--success)', bg: 'var(--success-bg)' },
                    { label: `${resyncUpdated.length} Hours/rate changed`, color: 'var(--warn)', bg: 'var(--warn-bg)' },
                    { label: `${resyncExisting.length} Unchanged`, color: 'var(--text3)', bg: 'var(--bg2)' },
                  ].map(c => (
                    <div key={c.label} style={{ padding: '4px 12px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 12, fontWeight: 600 }}>
                      {c.label}
                    </div>
                  ))}
                </div>

                {resyncNew.length === 0 && resyncUpdated.length === 0 ? (
                  <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                    ✓ All AxisCare visits are already in this billing run. Nothing to add or update.
                  </div>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead style={{ position: 'sticky', top: 0, background: 'var(--bg2)' }}>
                        <tr>
                          <th style={{ padding: '7px 10px', width: 32 }}>
                            <input type="checkbox"
                              checked={Object.values(resyncSelected).filter(Boolean).length === resyncVisits.filter((_,i) => resyncVisits[i]?.status !== 'existing').length}
                              onChange={e => {
                                const s = {};
                                resyncVisits.forEach((c, i) => { s[i] = c.status !== 'existing' ? e.target.checked : false; });
                                setResyncSelected(s);
                              }} />
                          </th>
                          {['Status','Client','Date','Code','Hrs (AxisCare)','Hrs (Current)','Change ($)'].map(h => (
                            <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {resyncVisits.map((item, i) => {
                          const { visit: v, status, existingLine } = item;
                          const h    = calcHours(v, resyncBasis);
                          const date = (v.actualStartDate || v.scheduledStartDate || v.startDate || '').slice(0, 10);
                          const client = v.client ? `${v.client.firstName || ''} ${v.client.lastName || ''}`.trim() : '—';
                          const newAmt = round2(h * (v.chargeRate || 0));
                          const oldAmt = existingLine ? (existingLine.accrued_amount || 0) : 0;
                          const diff   = round2(newAmt - oldAmt);
                          const isEditable = status !== 'existing';
                          const statusChip = status === 'new'
                            ? { label: 'New', bg: 'var(--success-bg)', color: 'var(--success)' }
                            : status === 'updated'
                            ? { label: 'Changed', bg: 'var(--warn-bg)', color: 'var(--warn)' }
                            : { label: 'No change', bg: 'var(--bg2)', color: 'var(--text3)' };
                          return (
                            <tr key={i} style={{ borderTop: '0.5px solid var(--border)', opacity: isEditable && !resyncSelected[i] ? 0.45 : 1 }}>
                              <td style={{ padding: '6px 10px' }}>
                                <input type="checkbox" checked={!!resyncSelected[i]}
                                  disabled={!isEditable}
                                  onChange={e => setResyncSelected(p => ({ ...p, [i]: e.target.checked }))} />
                              </td>
                              <td style={{ padding: '6px 10px' }}>
                                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20, background: statusChip.bg, color: statusChip.color }}>{statusChip.label}</span>
                              </td>
                              <td style={{ padding: '6px 10px', fontWeight: 500 }}>{client}</td>
                              <td style={{ padding: '6px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{date}</td>
                              <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: 11 }}>{v.service?.code || '—'}</td>
                              <td style={{ padding: '6px 10px' }}>{h.toFixed(1)}h</td>
                              <td style={{ padding: '6px 10px', color: 'var(--text3)' }}>{existingLine ? `${existingLine.hours}h` : '—'}</td>
                              <td style={{ padding: '6px 10px', fontWeight: 500, color: diff > 0 ? 'var(--success)' : diff < 0 ? 'var(--danger)' : 'var(--text3)' }}>
                                {status === 'new' ? `+${fmt$2(newAmt)}` : diff !== 0 ? `${diff > 0 ? '+' : ''}${fmt$2(diff)}` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {resyncStep === 'done' && resyncSummary && (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--success)', marginBottom: 16 }}>Resync Complete</div>
                <div style={{ display: 'flex', gap: 24, justifyContent: 'center' }}>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--success)' }}>{resyncSummary.added}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>New lines added</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--warn)' }}>{resyncSummary.updated}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Lines updated</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {resyncStep === 'date' && (
              <>
                <button className="btn btn-outline" onClick={closeResync}>Cancel</button>
                <button className="btn btn-primary" onClick={fetchResyncVisits} disabled={resyncFetching || !resyncFrom || !resyncTo}>
                  {resyncFetching ? 'Fetching…' : 'Fetch & Compare →'}
                </button>
              </>
            )}
            {resyncStep === 'review' && (
              <>
                <button className="btn btn-outline" onClick={() => setResyncStep('date')}>← Back</button>
                {(resyncNew.length > 0 || resyncUpdated.length > 0) && (
                  <button className="btn btn-primary" onClick={applyResync}
                    disabled={resyncing || !Object.values(resyncSelected).some(Boolean)}>
                    {resyncing ? 'Applying…' : `Apply ${Object.values(resyncSelected).filter(Boolean).length} Changes`}
                  </button>
                )}
                {resyncNew.length === 0 && resyncUpdated.length === 0 && (
                  <button className="btn btn-outline" onClick={closeResync}>Close</button>
                )}
              </>
            )}
            {resyncStep === 'done' && (
              <button className="btn btn-primary" onClick={closeResync}>Done</button>
            )}
          </div>
        </div>
      </div>
  );
}
